import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const RetrieveDefaultCategories = ({ onSectionChange }) => {
    return (
        <div className="api-reference-layout">
            <div className="api-content">
                <div className="api-header">
                    <div className="api-method-badge post">POST</div>
                    <h1>retrieve_default_categories - Retrieve Default Categories</h1>
                    <p className="api-description">
                        Retrieve user's default memory categories and their content
                    </p>
                </div>

                <div className="api-endpoint">
                    <div className="endpoint-url">
                        <span className="method post">POST</span>
                        <span className="url">/api/v2/memory/retrieve/default-categories</span>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="authorization">Authorization</h2>
                    <div className="auth-item">
                        <div className="auth-header">
                            <span className="auth-name">Authorization</span>
                            <span className="auth-type">string</span>
                            <span className="auth-location">header</span>
                            <span className="required-badge">required</span>
                        </div>
                        <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="request-body">Request Body</h2>
                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the user</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"user001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_id</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Agent identifier, if null retrieves for all agents</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"gpt001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">want_memory_items</span>
                                <span className="param-type">boolean</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Whether to return detailed memory items instead of summaries</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>false</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>true</code>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="response">Response</h2>
                    <div className="response-status">
                        <span className="status-code success">200</span>
                        <span className="content-type">application/json</span>
                    </div>

                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">categories</span>
                                <span className="param-type">array[CategoryResponse]</span>
                            </div>
                            <p>List of categories</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">total_categories</span>
                                <span className="param-type">integer</span>
                            </div>
                            <p>Total number of categories</p>
                        </div>
                    </div>

                    <div className="response-schema">
                        <h3>CategoryResponse Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">name</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category name</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">type</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category type</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">user_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>User ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">agent_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Agent ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">description</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category description</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">memory_items</span>
                                    <span className="param-type">CategoryMemoryItems</span>
                                </div>
                                <p>Memory item details (when want_memory_items=true)</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">summary</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category summary (when want_memory_items=false)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="api-demo-sidebar">
                <div className="demo-sticky">
                    <div className="demo-header">
                        <div className="demo-tabs">
                            <button className="demo-tab active">Python</button>
                            <button className="demo-tab">JavaScript</button>
                            <button className="demo-tab">cURL</button>
                        </div>
                    </div>

                    <div className="demo-content">
                        <h3 id="examples">Request Example</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`import os
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Retrieve default categories with summaries
response = memu_client.retrieve_default_categories(
    user_id="user001",
    agent_id="gpt001",
    want_memory_items=False
)

print(f"Total categories: {response.total_categories}")
for category in response.categories:
    print(f"Category: {category.name}")
    print(f"Summary: {category.summary}")

# Retrieve with detailed memory items
detailed_response = memu_client.retrieve_default_categories(
    user_id="user001",
    agent_id="gpt001",
    want_memory_items=True
)

for category in detailed_response.categories:
    print(f"Category: {category.name}")
    print(f"Memory items: {category.memory_items}")`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response (Summary Mode)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "categories": [
    {
      "name": "Personal Preferences",
      "type": "preference",
      "user_id": "user001",
      "agent_id": "gpt001",
      "description": "User's personal preferences and likes",
      "summary": "User enjoys hiking, prefers sci-fi movies, and likes Italian food"
    },
    {
      "name": "Work & Career",
      "type": "professional",
      "user_id": "user001",
      "agent_id": "gpt001",
      "description": "Professional and work-related information",
      "summary": "Software engineer at tech company, works on AI projects"
    }
  ],
  "total_categories": 2
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response (Detailed Mode)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "categories": [
    {
      "name": "Personal Preferences",
      "type": "preference",
      "user_id": "user001",
      "agent_id": "gpt001",
      "description": "User's personal preferences and likes",
      "memory_items": {
        "items": [
          {
            "content": "User loves hiking in mountains",
            "timestamp": "2024-01-15T10:30:00Z"
          },
          {
            "content": "Prefers sci-fi movies over other genres",
            "timestamp": "2024-01-16T14:20:00Z"
          }
        ],
        "count": 2
      }
    }
  ],
  "total_categories": 1
}`}
                        </SyntaxHighlighter>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RetrieveDefaultCategories;

